import React, { useEffect, useMemo, useRef, useState } from "react";
import ellip from "../assets/images/ellip.svg";
import affiinviteimg from "../assets/images/affi-invite-img.svg";
import inviteGift from "../assets/images/invite-gift.svg";
import QR from "../assets/images/qr.png";
import affiBonus from "../assets/images/affi-bonus.svg";
import affiBonus2 from "../assets/images/affi-bonus-2.svg";
import ShareDialog from "./ShareDialog";
import ShareAffiliateLinkDialog from "./ShareAffiliateLinkDialog";
import Dialog from "../shared/Dialog";
import { Copy, Plus } from "../shared/icon";
import axiosInstance from "../api/axiosInstance";
import { useSelector } from "react-redux";
import { getUserData, isAuthenticated } from "../utils/auth";
import { toast } from "react-toastify";
import QRCode from "react-qr-code";

const DashboardTab = () => {
  const [shareDialog, setShareDialog] = useState(false);
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showHowToGetBonusModal, setShowHowToGetBonusModal] = useState(false);
  const [bonusSlabs, setBonusSlabs] = useState([]);
  const [affiliateData, setAffiliateData] = useState({
    total_users: 0,
    total_affiliate_comm: 0,
    total_deposit_amount: 0,
    total_deposit_count: 0,
  });
  const [plData, setPlData] = useState([
    { type: "Sports", pl: 0 },
    { type: "Casino", pl: 0 },
    { type: "International Casino (QT)", pl: 0 },
  ]);

  const [referCode, setReferCode] = useState();
  const [affCode, setAffCode] = useState();
  const [loading, setLoading] = useState(false);
  const qrContainerRef = useRef(null);

  const authUser = useSelector((state) => state.auth?.user);
  const isLoggedIn = typeof window !== "undefined" ? isAuthenticated() : false;
  const isDemoUser = authUser?.is_demo === true;
  // Block API calls until we know whether the user is demo.
  const canAccess = isLoggedIn;

  const [userData, setUserData] = useState(() => {
    if (typeof window === "undefined") return null;
    return getUserData();
  });

  const baseInviteDomain = useMemo(() => {
    if (!canAccess) return "https://usabet9.com";
    return typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://usabet9.com";
  }, [canAccess]);

  const inviteLink = useMemo(() => {
    if (userData?.user_name)
      return `${baseInviteDomain}/signup?aff_code=${userData?.user_name}`;
    return "";
  }, [userData, baseInviteDomain]);

  const formatNumber = (val) => {
    const num = typeof val === "number" ? val : Number(val ?? 0);
    if (!Number.isFinite(num)) return "0";
    return num.toLocaleString();
  };

  const downloadInviteQrPng = async () => {
    if (!inviteLink) {
      return;
    }
    const container = qrContainerRef.current;
    const svg = container?.querySelector?.("svg");
    if (!svg) {
      toast.error("QR code is not available");
      return;
    }

    try {
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svg);
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      const pngUrl = await new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            const scale = 6; // crisp download
            const size = Number(svg.getAttribute("width") || 100);
            const canvas = document.createElement("canvas");
            canvas.width = size * scale;
            canvas.height = size * scale;
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("No canvas context");
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/png"));
          } catch (e) {
            reject(e);
          } finally {
            URL.revokeObjectURL(svgUrl);
          }
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(svgUrl);
          reject(e);
        };
        img.src = svgUrl;
      });

      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = "affiliate-invite-qr.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("QR code downloaded");
    } catch {
      toast.error("Failed to download QR code");
    }
  };

  useEffect(() => {
    if (!canAccess) return;

    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [countRes, plRes, affCodeRes, bonusRes] = await Promise.all([
          axiosInstance.post("/report/getAffUserCount", {}),
          axiosInstance.post("/report/getAffUserTodayPL", {}),
          axiosInstance.post("/report/getUserAffCode", {}),
          axiosInstance.post("/report/getAffBonusSetting", {}),
        ]);

        if (!mounted) return;

        if (countRes?.status && countRes?.data) {
          setAffiliateData(countRes.data);
        }

        const plArray =
          plRes?.status && Array.isArray(plRes?.data?.response)
            ? plRes.data.response
            : plRes?.status && Array.isArray(plRes?.data)
              ? plRes.data
              : [];
        setPlData(plArray);

        if (affCodeRes?.status && affCodeRes?.data) {
          setAffCode(affCodeRes.data.affiliate_code || "");
          setReferCode(affCodeRes.data.refer_code || "");
        }

        if (bonusRes?.status && Array.isArray(bonusRes?.data?.bonus_slabs)) {
          setBonusSlabs(bonusRes.data.bonus_slabs);
        } else {
          setBonusSlabs([]);
        }
      } catch {
        // Keep existing UI defaults on failure.
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [canAccess]);

  return (
    <>
      <div className="affiliate-dashboard">
        <section className="affiliate-card">
          <img src={ellip} className="card-bg" alt="" />

          <h3 className="card-title">Today Status</h3>

          <div className="status-grid">
            <div className="status-item">
              <span className="status-value">
                {loading
                  ? "-"
                  : formatNumber(affiliateData.total_deposit_amount)}
              </span>
              <p>Total Deposit</p>
            </div>
            <div className="status-item">
              <span className="status-value">
                {loading
                  ? "-"
                  : formatNumber(affiliateData.total_deposit_count)}
              </span>
              <p>Total Deposit Count</p>
            </div>
            <div className="status-item">
              <span className="status-value">
                {loading ? "-" : formatNumber(affiliateData.total_users)}
              </span>
              <p>Total User</p>
            </div>
            <div className="status-item">
              <span className="status-value">
                {loading
                  ? "-"
                  : formatNumber(affiliateData.total_affiliate_comm)}
              </span>
              <p>Total Commission</p>
            </div>
          </div>
        </section>

        <section className="invite-card">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="invite-content">
              <h4>Invite your friends</h4>
              <p className="mb-0">To join and receive huge bonuses</p>
            </div>

            <img src={affiinviteimg} alt="" className="invite-image" />
          </div>

          <button
            className="primary-btn"
            onClick={() => {
              if (!canAccess) return;
              setShareDialog(true);
            }}
          >
            <Plus />
            ADD NEW USER
          </button>
          <div className="qr-card">
            <h5>
              <img className="me-2" src={inviteGift} alt="" />
              Invitation Code
            </h5>

            <div className="invite-link">
              {loading ? "-" : inviteLink || "-"}
            </div>

            <div className="qr-actions">
              <div
                className="qr-box"
                onClick={() => {
                  if (!canAccess || !inviteLink) {
                    toast.error("QR code is not available");
                    return;
                  }
                  downloadInviteQrPng();
                }}
              >
                <div ref={qrContainerRef}>
                  <QRCode value={inviteLink} size={100} />
                </div>

                {/* <img className="qr" src={QR} alt="" /> */}
                <span className="download-text">Download</span>
              </div>

              <div className="action-buttons">
                <button
                  className="secondary-btn"
                  type="button"
                  onClick={async () => {
                    if (!canAccess || !inviteLink) return;
                    try {
                      if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(inviteLink);
                      } else {
                        const textarea = document.createElement("textarea");
                        textarea.value = inviteLink;
                        textarea.style.position = "fixed";
                        textarea.style.opacity = "0";
                        document.body.appendChild(textarea);
                        textarea.focus();
                        textarea.select();
                        document.execCommand("copy");
                        document.body.removeChild(textarea);
                      }
                      toast.success("Link copied to clipboard");
                    } catch {
                      // no-op
                    }
                  }}
                >
                  <Copy /> Copy Link
                </button>
                <button
                  className="share-btn"
                  type="button"
                  onClick={() => {
                    if (!canAccess) return;
                    setShareLinkDialogOpen(true);
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="nw-affi-graph-wrapper">
          <div className="nw-affi-graph-box">
            <h4 className="nw-affi-heading-text">Top 5 Loss Users</h4>
          </div>
        </div>

        <section className="affiliate-card">
          <img src={ellip} className="card-bg" alt="" />
          <h3 className="card-title">Bonus Information</h3>

          <div className="bonus-grid">
            <div
              className="bonus-info"
              onClick={() => {
                // if (!canAccess) return;
                setShowHowToGetBonusModal(true);
              }}
              style={{ cursor: canAccess ? "pointer" : "default" }}
            >
              <img src={affiBonus} alt="" />
              <p>How to get bonus?</p>
            </div>
            <div
              className="bonus-info"
              onClick={() => {
                // if (!canAccess) return;
                setShowBonusModal(true);
              }}
              style={{ cursor: canAccess ? "pointer" : "default" }}
            >
              <img src={affiBonus2} alt="" />
              <p>Commission Info</p>
            </div>
          </div>
        </section>
        <div className="af-top-user-sec">
          <div className="af-header">
            <h6 className="af-heading">Today Profit and Loss</h6>
          </div>

          <table className="af-table">
            <thead>
              <tr>
                <th className="text-start">Type</th>
                <th className="text-center">PL</th>
              </tr>
            </thead>
            <tbody id="plTableBody">
              {loading ? (
                <tr>
                  <td colSpan="2" className="text-center py-3">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Loading...
                  </td>
                </tr>
              ) : plData.length === 0 ? (
                <tr>
                  <td colSpan="2" className="text-center py-3 text-muted">
                    No data available
                  </td>
                </tr>
              ) : (
                plData.map((item, index) => (
                  <tr key={index}>
                    <td className="text-start">{item.type || "N/A"}</td>
                    <td className="text-center green-text">
                      {item.pl !== undefined ? item.pl : 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog
        show={shareDialog && canAccess}
        onHide={() => setShareDialog(false)}
        centered
        animation={false}
        dialogClassName="bonus-dialog"
        contentClassName="mat-dialog-container"
      >
        <ShareDialog onClose={() => setShareDialog(false)} />
      </Dialog>

      <Dialog
        show={shareLinkDialogOpen && canAccess}
        onHide={() => setShareLinkDialogOpen(false)}
        centered
        animation={false}
        dialogClassName="share-affiliate-dialog"
        contentClassName="mat-dialog-container"
      >
        <ShareAffiliateLinkDialog
          open={shareLinkDialogOpen && canAccess}
          onClose={() => setShareLinkDialogOpen(false)}
        />
      </Dialog>

      <Dialog
        show={showBonusModal}
        onHide={() => setShowBonusModal(false)}
        centered
        animation={false}
        dialogClassName="bonus-dialog"
        contentClassName="mat-dialog-container"
      >
        <div style={{ padding: "20px", borderRadius: "50px" }}>
          <h3 style={{ marginBottom: "12px", fontWeight: "semibold" }}>
            Bonus Information
          </h3>
          <p>
            You get bonus of the total amount your users lose every day based on
            below slabs.
          </p>
          <p>The system checks daily at 1:00 AM.</p>
          <p>If your users lost money overall, you get paid.</p>
          <p>If your users made profit, no bonus is given for that day.</p>

          <h4 style={{ marginTop: "16px", marginBottom: "8px" }}>Example</h4>
          <table className="af-table default-table">
            <thead>
              <tr>
                <th>User Range</th>
                <th>Bonus (%)</th>
              </tr>
            </thead>
            <tbody>
              {bonusSlabs && bonusSlabs.length > 0 ? (
                bonusSlabs.map((slab, index) => {
                  const isLast = index === bonusSlabs.length - 1;
                  const rangeLabel = isLast
                    ? `${slab.start_range}-${slab.end_range}+ Users`
                    : `${slab.start_range}-${slab.end_range} Users`;
                  return (
                    <tr key={`${slab.start_range}-${slab.end_range}-${index}`}>
                      <td>{rangeLabel}</td>
                      <td>{slab.percentage_amount}%</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={2} className="no-records">
                    No bonus slabs configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Dialog>

      <Dialog
        show={showHowToGetBonusModal}
        onHide={() => setShowHowToGetBonusModal(false)}
        centered
        animation={false}
        dialogClassName="bonus-dialog"
        contentClassName="mat-dialog-container"
      >
        <div style={{ padding: "20px", borderRadius: "50px" }}>
          <h3 style={{ marginBottom: "20px", fontWeight: "semibold" }}>
            How to get bonus?
          </h3>

          <div
            className="affi-how-bonus-list affi-how-to-get-bonus"
            style={{ height: "80vh", overflow: "auto" }}
          >
            <h5>🧾 Affiliate Program – How It Works</h5>
            <ul style={{ marginBottom: "20px" }}>
              <li>Earn daily bonus by inviting users to play!</li>
            </ul>

            <h5>🎯 How to Add Users</h5>
            <p>You can add users in two easy ways:</p>

            <h6>1️⃣ Referral Link</h6>
            <ul style={{ marginBottom: "20px" }}>
              <li>Your unique invitation link is shown on your dashboard.</li>
              <li>
                Anyone who signs up using this link will be automatically added
                to your team.
              </li>
              <li>You can copy, share, or download a QR code.</li>
            </ul>

            <h6>2️⃣ Add New User (Manual)</h6>
            <ul style={{ marginBottom: "20px" }}>
              <li>
                Tap on &quot;Add New User&quot; and fill in the user&apos;s
                info.
              </li>
              <li>The user will instantly be created under your downline.</li>
            </ul>

            <h5>📊 Your Affiliate Panel Features</h5>

            <h6>🔹Dashboard</h6>
            <ul style={{ marginBottom: "20px" }}>
              <li>Total users added</li>
              <li>Total users who deposited</li>
              <li>Total commission earned</li>
              <li>Today’s top 5 losing users</li>
              <li>Referral link &amp; QR code</li>
              <li>Add user button</li>
            </ul>

            <h6>🔹User List</h6>
            <ul style={{ marginBottom: "20px" }}>
              <li>See all your downline users</li>
              <li>View their balance and registration date</li>
              <li>Tap &quot;Add New User&quot; to add more</li>
              <li>Filter users by registration date</li>
            </ul>

            <h6>🔹Profit &amp; Loss</h6>
            <ul style={{ marginBottom: "20px" }}>
              <li>See user profit/loss by date range</li>
              <li>Breakdown by event (casino, sports, etc.)</li>
              <li>Total profit, total loss, and net P&amp;L</li>
              <li>This is used to calculate your daily bonus</li>
            </ul>

            <h6>🔹Reports</h6>
            <ul style={{ marginBottom: "20px" }}>
              <li>View daily commission report</li>
              <li>Check how much bonus you earned each day</li>
              <li>Filter by date range</li>
            </ul>

            <h5>📌 Important Rules</h5>
            <p>
              ✅ You earn commission only if your users lost money overall for
              that day.
            </p>
            <p>✅ Bonus is added daily after 2:00 AM automatically.</p>
            <p>
              ✅ No commission if user total P&amp;L is positive (i.e., users
              made profit).
            </p>
            <p>
              ✅ You can use both link and manual user addition — both work the
              same.
            </p>
            <p>
              🚫 Don&apos;t misuse the system or create fake users — this may
              result in suspension.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default DashboardTab;
